import { usePreferencesProvider } from "@jugl-web/domain-resources/preferences";
import { useUserEntityPreferences } from "@jugl-web/domain-resources/preferences/hooks/useUserEntityPreferences";
import { ChatType, SoundSupportedModule } from "@jugl-web/rest-api";
import {
  getUniqueId,
  trimHTML,
  useAppVariant,
  useTabIsActive,
  useTranslations,
} from "@jugl-web/utils";
import useEntity from "@web-src/modules/entities/hooks/useEntity";
import { selectUserId } from "@web-src/modules/auth/store/authSlice";
import { extractNumberFromSoundType } from "@web-src/modules/settings/pages/SettingsPage/components/NotificationsSettingsSidebar/utils/extractNumberFromSoundType";
import { useSnackbar } from "notistack";
import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { MENTIONS_ALL_ID } from "@jugl-web/utils/consts";
import { environment } from "@web-src/environments/environment";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { FCMProvider } from "../FCMProvider";
import { NotificationsPermissionsPopup } from "./components/NotificationsPermissionsPopup/NotificationsPermissionsPopup";
import {
  InAppNotification,
  NotificationsContext,
  NotificationsContextValue,
  TriggerNotificationParams,
} from "./NotificationsContext";
import { ticketNotificationUpdates$ } from "./ticketNotificationUpdates";

const MAIN_TAB_STORAGE_KEY = "JUGL:NOTIFICATIONS_MAIN_TAB";

const isMainTab = (tabKey?: string) =>
  tabKey && localStorage.getItem(MAIN_TAB_STORAGE_KEY) === tabKey;

const TICKET_GROUP_WINDOW_MS = 30_000;

type TicketNotificationGroup = {
  pushTag: string;
  sessionId: string;
  count: number;
  timeoutId: ReturnType<typeof setTimeout>;
  entityId: string;
  ticketId: string;
  userName: string;
  ticketTitle: string;
  isSnackbarVisible: boolean;
  onClick?: () => void;
};

export const NotificationsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [tabKey, setTabKey] = useState<string>();
  const tabIsActive = useTabIsActive();

  useEffect(() => {
    if (!tabIsActive) {
      return;
    }
    const key = getUniqueId();
    localStorage.setItem(MAIN_TAB_STORAGE_KEY, key);
    setTabKey(key);
  }, [tabIsActive]);

  const [canPlaySound, setCanPlaySound] = useState<boolean>(false);
  useEffect(() => {
    const mouseOverCallback = () => {
      setCanPlaySound(true);
      document.body.removeEventListener("click", mouseOverCallback);
    };
    document.body.addEventListener("click", mouseOverCallback);
  }, []);

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslations();
  const ticketGroupsRef = useRef<Map<string, TicketNotificationGroup>>(
    new Map()
  );
  const meId = useSelector(selectUserId);
  const [shownNotifications, setShownNotifications] = useState<{
    [key: string]: boolean;
  }>({});
  const location = useLocation();
  const { isCx } = useAppVariant();
  const { preferences: appPreferences } = usePreferencesProvider();

  // Notification status and popup is now managed in NotificationsPermissionsPopup component

  const { entity } = useEntity();
  const { isAnonymous } = useAnonymousSession();
  const { preferences: userPreferences, updatePreferences } =
    useUserEntityPreferences({
      entityId: entity?.id,
      skip: isAnonymous,
    });
  const notificationPreferences = useMemo(
    () => userPreferences?.notification_pref,
    [userPreferences]
  );

  const triggerPushNotification = useCallback(
    (title: string, params?: TriggerNotificationParams) => {
      if (document.visibilityState === "visible") {
        return;
      }

      // Check if notifications are supported and permitted
      if (
        !("Notification" in window) ||
        Notification.permission !== "granted"
      ) {
        return;
      }

      // oxlint-disable-next-line no-new
      const notification = new Notification(title, {
        body: params?.body,
        tag: params?.tag,
      });
      notification.onclick = () => {
        window.focus();
        params?.onClick?.();
      };
    },
    []
  );

  const playNotificationSoundForModule = useCallback(
    (type?: SoundSupportedModule) => {
      if (
        (!isCx &&
          type &&
          userPreferences?.notification_pref?.[type]?.sound === false) ||
        !canPlaySound
      ) {
        return;
      }
      const notificationSoundId =
        type && !isCx
          ? (extractNumberFromSoundType(
              userPreferences?.notification_pref?.[type]?.sound_web
            ) ?? 0)
          : 0;
      new Audio(
        `/notification-sounds/notification${notificationSoundId || ""}.mp3`
      ).play();
    },
    [canPlaySound, isCx, userPreferences?.notification_pref]
  );

  const getChatNotificationsPreferences = useCallback<
    NotificationsContextValue["getChatNotificationsPreferences"]
  >(
    (chatId) =>
      notificationPreferences?.chats?.except?.[chatId]?.mute ||
      notificationPreferences?.groups?.except?.[chatId]?.mute ||
      "none",
    [notificationPreferences]
  );

  const isNotificationInSameModule = useCallback(
    (notification: InAppNotification) => {
      if (notification.module === "chats" || notification.module === "groups") {
        return location.pathname.includes("/chats");
      }
      if (notification.module === "drive") {
        return location.pathname.includes("/drive");
      }
      if (notification.module === "task") {
        // TODO: add entityId to notification
        return location.pathname.includes("/tasks");
      }
      if (notification.module === "crm" || notification.module === "ticket") {
        return location.pathname.includes("/client-support");
      }
      return false;
    },
    [location.pathname]
  );

  const isChatNotificationMuted = useCallback(
    (chatId: string, body: string) => {
      const chatNotificationPreferences =
        getChatNotificationsPreferences(chatId);
      if (chatNotificationPreferences === "all") {
        return true;
      }
      if (chatNotificationPreferences === "all_except_mentions") {
        return (
          (!meId || !body.includes(meId)) && !body.includes(MENTIONS_ALL_ID)
        );
      }
      return false;
    },
    [getChatNotificationsPreferences, meId]
  );

  useEffect(() => {
    const groups = ticketGroupsRef.current;
    return () => {
      groups.forEach((group) => clearTimeout(group.timeoutId));
      groups.clear();
    };
  }, []);

  const buildGroupedTicketNotification = useCallback(
    (group: TicketNotificationGroup): InAppNotification => ({
      entityId: group.entityId,
      module: "ticket",
      title: group.ticketTitle,
      body: t(
        {
          id: "in-app-notification-snackbar-component.grouped-ticket-updates",
          defaultMessage:
            "{userName} made {count, plural, one {# update} other {# updates}} on ticket {title}",
        },
        {
          userName: `<b>${group.userName}</b>`,
          count: group.count,
          title: `<b>${group.ticketTitle}</b>`,
        }
      ),
      ticketId: group.ticketId,
      onClick: group.onClick,
    }),
    [t]
  );

  const markTicketSnackbarHidden = useCallback((sessionId: string) => {
    const group = Array.from(ticketGroupsRef.current.values()).find(
      (entry) => entry.sessionId === sessionId
    );

    if (group) {
      group.isSnackbarVisible = false;
    }
  }, []);

  const enqueueNotificationSnackbar = useCallback(
    (sessionId: string, notification: InAppNotification) => {
      enqueueSnackbar("", {
        key: sessionId,
        variant: "inAppNotification",
        notification,
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
        // Dismissal is handled inside InAppNotificationSnackbar (5s timeout),
        // not by notistack — applies to every notification using this variant.
        autoHideDuration: null,
        transitionDuration: 300,
        onClose:
          notification.module === "ticket"
            ? () => markTicketSnackbarHidden(sessionId)
            : undefined,
      });
    },
    [enqueueSnackbar, markTicketSnackbarHidden]
  );

  const refreshTicketGroupTimeout = useCallback((groupKey: string) => {
    const groups = ticketGroupsRef.current;
    const group = groups.get(groupKey);
    if (!group) {
      return;
    }

    clearTimeout(group.timeoutId);
    group.timeoutId = setTimeout(() => {
      groups.delete(groupKey);
    }, TICKET_GROUP_WINDOW_MS);
  }, []);

  const handleTicketNotification = useCallback(
    (
      notification: InAppNotification,
      { showSnackbar = true }: { showSnackbar?: boolean } = {}
    ) => {
      const groups = ticketGroupsRef.current;
      const { entityId, ticketId, userId, userName } = notification;
      const hasGroupingData = Boolean(entityId && ticketId && userId);
      const groupKey = hasGroupingData
        ? `${entityId}:${ticketId}:${userId}`
        : null;
      const existingGroup = groupKey ? groups.get(groupKey) : undefined;

      if (!existingGroup || !groupKey) {
        const pushTag = getUniqueId();
        const sessionId = getUniqueId();

        if (hasGroupingData && groupKey && ticketId && userName) {
          groups.set(groupKey, {
            pushTag,
            sessionId,
            count: 1,
            timeoutId: setTimeout(() => {
              groups.delete(groupKey);
            }, TICKET_GROUP_WINDOW_MS),
            ticketTitle: notification.title,
            entityId,
            ticketId,
            userName,
            onClick: notification.onClick,
            isSnackbarVisible: showSnackbar,
          });
        }

        if (document.hidden && isMainTab(tabKey)) {
          triggerPushNotification(notification.title, {
            tag: pushTag,
            body: trimHTML(notification.body || ""),
            onClick: notification.onClick,
          });
        }

        if (showSnackbar) {
          enqueueNotificationSnackbar(sessionId, notification);
        }

        return;
      }

      existingGroup.count += 1;
      refreshTicketGroupTimeout(groupKey);

      const groupedNotification = buildGroupedTicketNotification(existingGroup);

      if (document.hidden && isMainTab(tabKey)) {
        triggerPushNotification(groupedNotification.title, {
          tag: existingGroup.pushTag,
          body: trimHTML(groupedNotification.body || ""),
          onClick: groupedNotification.onClick,
        });
      }

      if (!showSnackbar) {
        return;
      }

      if (existingGroup.isSnackbarVisible) {
        ticketNotificationUpdates$.next({
          sessionId: existingGroup.sessionId,
          notification: groupedNotification,
        });
        return;
      }

      existingGroup.isSnackbarVisible = true;
      existingGroup.sessionId = getUniqueId();
      enqueueNotificationSnackbar(existingGroup.sessionId, groupedNotification);
    },
    [
      buildGroupedTicketNotification,
      enqueueNotificationSnackbar,
      refreshTicketGroupTimeout,
      tabKey,
      triggerPushNotification,
    ]
  );

  const triggerInAppNotification = useCallback(
    (notification: InAppNotification) => {
      if (notification.key) {
        if (shownNotifications[notification.key]) {
          return;
        }
        setShownNotifications({
          ...shownNotifications,
          [notification.key]: true,
        });
      }
      // TODO: revisit
      if (
        !isCx &&
        notification.module &&
        userPreferences?.notification_pref?.[notification.module]?.noti ===
          false
      ) {
        return;
      }
      if (
        (notification.module === "chats" || notification.module === "groups") &&
        notification.senderId &&
        isChatNotificationMuted(
          notification.senderId,
          notification.clearBody || ""
        )
      ) {
        return;
      }
      if (!isCx && !appPreferences.inAppNotificationsEnabled) {
        return;
      }
      const isInSameModule = isNotificationInSameModule(notification);
      if ((document.hidden || !isInSameModule) && isMainTab(tabKey)) {
        playNotificationSoundForModule?.(notification.module);
      }

      if (notification.module === "ticket") {
        handleTicketNotification(notification, {
          showSnackbar: !isInSameModule,
        });
      } else {
        if (document.hidden && isMainTab(tabKey)) {
          triggerPushNotification?.(notification.title, {
            body: trimHTML(notification.body || ""),
            onClick: notification.onClick,
          });
        }

        if (!isInSameModule) {
          enqueueNotificationSnackbar("1", notification);
        }
      }
    },
    [
      userPreferences?.notification_pref,
      isChatNotificationMuted,
      isCx,
      appPreferences.inAppNotificationsEnabled,
      isNotificationInSameModule,
      shownNotifications,
      playNotificationSoundForModule,
      triggerPushNotification,
      enqueueNotificationSnackbar,
      handleTicketNotification,
      tabKey,
    ]
  );

  // TODO: move out to user preferences module
  const setChatNotificationsPreferences = useCallback<
    NotificationsContextValue["setChatNotificationsPreferences"]
  >(
    (chatId, chatType, option) => {
      if (!entity?.id) {
        return;
      }
      updatePreferences({
        notification_pref:
          chatType === ChatType.muc
            ? { groups: { except: { [chatId]: { mute: option } } } }
            : { chats: { except: { [chatId]: { mute: option } } } },
      });
    },
    [entity, updatePreferences]
  );

  const contextValue = useMemo<NotificationsContextValue>(
    () => ({
      triggerInAppNotification,
      getChatNotificationsPreferences,
      setChatNotificationsPreferences,
      notificationPreferences,
    }),
    [
      triggerInAppNotification,
      getChatNotificationsPreferences,
      setChatNotificationsPreferences,
      notificationPreferences,
    ]
  );

  return (
    <NotificationsContext.Provider value={contextValue}>
      {entity && !environment.isDemo && !isAnonymous && (
        <NotificationsPermissionsPopup />
      )}
      <FCMProvider>{children}</FCMProvider>
    </NotificationsContext.Provider>
  );
};
